// ADR 8.10.26z §3 — the locked tutor FAB, measured at the tap in 15.9.26ad's
// shape: `upgrade_prompt_shown` latched once per page session, then
// `upgrade_prompt_converted` on every press of the snackbar's Upgrade action.
//
// `tutor_teaser` is the trigger the phones already use for this FAB
// (`TutorHost.kt`, `AppNavigation+Spread.swift`), but the phones measure it
// differently — a throttle decision, then `subscription_screen_viewed` with
// `source: tutor_teaser` — so the web's row on `sub-funnel-prompt-conversion`
// is read on its own. No throttle here, so never `upgrade_prompt_suppressed`
// (8.10.26y's "no throttle on the FAB snackbar").
//
// Names are never literals: billing's and vocab's constants, one spelling each.

import { capture } from '../../../telemetry/capture'
import { BILLING_EVENTS } from '../../billing/analytics'
import { type AnalyticsSink, VOCAB_EVENTS } from '../../vocab/analytics'

/** The phones' trigger for the locked tutor FAB. */
export const TUTOR_TEASER_TRIGGER = 'tutor_teaser'

/**
 * Module-level, not a `useRef` — `doorShown`'s reason in `vocab/analytics.ts`:
 * StrictMode mounts twice in development, and a per-mount latch would report
 * one tap as two.
 */
let shownThisSession = false

const defaultSink: AnalyticsSink = (name, properties) => {
  capture(name, properties)
}

let sink: AnalyticsSink = defaultSink

/** Test seam: watch the events without attaching an SDK. */
export function setLockedFabSink(replacement: AnalyticsSink): void {
  sink = replacement
}

export function resetLockedFabSink(): void {
  sink = defaultSink
}

/** Test seam: a fresh page session for the tap latch. */
export function resetLockedFabLatch(): void {
  shownThisSession = false
}

/** The locked FAB was tapped — `shown`, once per page session. */
export function lockedFabTapped(): void {
  if (shownThisSession) return
  shownThisSession = true
  sink(BILLING_EVENTS.promptShown, { trigger: TUTOR_TEASER_TRIGGER })
}

/** The snackbar's Upgrade action was taken — every time. */
export function lockedFabUpgradeTaken(): void {
  sink(VOCAB_EVENTS.upgradePromptConverted, {
    trigger: TUTOR_TEASER_TRIGGER,
    target: 'subscription_screen',
  })
}
